import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL from '../config/api';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Divider,
  Slider
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import UsbIcon from '@mui/icons-material/Usb';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import { BluetoothPrinterManager } from '../utils/printHelper';
import { useApp } from '../context/AppContext';

// English, Marathi, Hindi translations database for restaurant receipt printing
const TRANSLATIONS = {
  mr: {
    companyName: "स्वाद महाराष्ट्रीयन ढाबा",
    address: "NH-४ हायवे, कोल्हापूर बायपास",
    tel: "Tel: +९१ ९८७६५४३२१०",
    date: "दिनांक",
    time: "वेळ",
    token: "टोकन",
    type: "प्रकार",
    payment: "पेमेंट",
    item: "पदार्थ",
    qty: "नग",
    amt: "किंमत",
    subtotal: "एकूण मुळ किंमत",
    gst: "GST (५%)",
    discount: "सूट / डिस्काउंट",
    totalPaid: "एकूण बिल रक्कम",
    thankYou: "धन्यवाद! पुन्हा भेटूया!",
    kotHeader: "किचन ऑर्डर तिकीट (KOT)",
    table: "टेबल",
    waiter: "वेटर्स",
    kotNo: "KOT नं",
    instruction: "सूचना",
    poweredBy: "Dinexa Smart POS Kitchen Hub",
    collectionHeader: "शिवशंभु दुध संकलन",
    subHeader: "सांगवड",
    farmerName: "सभासद नाव",
    codeNo: "कोड क्र.",
    milkType: "दूध प्रकार",
    fat: "फॅट",
    snf: "SNF",
    liters: "लिटर",
    rate: "दर रू.",
    totalAmt: "रक्कम रु"
  },
  hi: {
    companyName: "स्वाद महाराष्ट्रीयन ढाबा",
    address: "NH-4 हाईवे, कोल्हापुर बाईपास",
    tel: "Tel: +91 9876543210",
    date: "दिनांक",
    time: "समय",
    token: "टोकन",
    type: "प्रकार",
    payment: "भुगतान",
    item: "सामग्री",
    qty: "मात्रा",
    amt: "मूल्य",
    subtotal: "कुल मूल्य",
    gst: "GST (5%)",
    discount: "छूट",
    totalPaid: "कुल भुगतान",
    thankYou: "धन्यवाद! फिर मिलेंगे!",
    kotHeader: "रसोई ऑर्डर टिकट (KOT)",
    table: "टेबल",
    waiter: "वेंडर / वेटर",
    kotNo: "KOT संख्या",
    instruction: "निर्देश",
    poweredBy: "Dinexa Smart POS Kitchen Hub",
    collectionHeader: "दूध संग्रह पावती (Collection Receipt)",
    farmerName: "किसान का नाम",
    codeNo: "कोड सं.",
    milkType: "दूध का प्रकार",
    fat: "फैट (FAT %)",
    snf: "एस.एन.एफ (SNF %)",
    liters: "कुल लीटर (Ltr)",
    rate: "दर (रु./लीटर)",
    totalAmt: "कुल राशि (रु.)"
  },
  en: {
    companyName: "SWAD MAHARASHTRIAN DHABA",
    address: "NH-4 Highway, Kolhapur Bypass",
    tel: "Tel: +91 9876543210",
    date: "Date",
    time: "Time",
    token: "TOKEN",
    type: "Type",
    payment: "Payment",
    item: "Item",
    qty: "Qty",
    amt: "Amt",
    subtotal: "Subtotal",
    gst: "GST (5%)",
    discount: "Discount",
    totalPaid: "TOTAL PAID",
    thankYou: "THANK YOU! VISIT AGAIN",
    kotHeader: "KITCHEN ORDER TICKET",
    table: "TABLE",
    waiter: "Waiter",
    kotNo: "KOT No",
    instruction: "Instruction",
    poweredBy: "Dinexa Smart POS Kitchen Hub",
    collectionHeader: "MILK COLLECTION RECEIPT",
    farmerName: "Farmer Name",
    codeNo: "Code No",
    milkType: "Milk Type",
    fat: "FAT %",
    snf: "SNF %",
    liters: "Liters",
    rate: "Rate",
    totalAmt: "Total Amount"
  }
};

const SAMPLE_COUPON1_DATA = {
  id: "ORD-04182753-377",
  coupon_number: 7,
  token_number: 7,
  timestamp: new Date().toISOString(),
  hotelName: "HOTEL MARDANI",
  branchName: "Branch Saidapur",
  items: [
    { name: "Ukala", quantity: 1, price: 20, total: 20 }
  ],
  total: 20
};

const SAMPLE_COUPON2_DATA = {
  id: "ORD-04182753-378",
  coupon_number: 7,
  token_number: 7,
  timestamp: new Date().toISOString(),
  hotelName: "HOTEL MARDANI",
  branchName: "Branch Saidapur",
  items: [
    { name: "Misal Pav (मिसळ पाव)", quantity: 1, price: 60, total: 60 },
    { name: "Special Tea (चहा)", quantity: 2, price: 15, total: 30 }
  ],
  total: 90
};

const SAMPLE_KOT_DATA = {
  id: "KOT-902",
  timestamp: new Date().toISOString(),
  tableNumber: "5",
  waiterName: "Counter Staff",
  items: [
    { name: "Paneer Tikka (पनीर टिक्का)", quantity: 2, remarks: "Spicy (तिखट)" },
    { name: "Tandoori Roti (तंदुरी रोटी)", quantity: 4, remarks: "Butter (लोणी लावून)" },
    { name: "Veg Biryani (व्हेज बिर्याणी)", quantity: 1, remarks: "" }
  ]
};

const SAMPLE_BILL_DATA = {
  id: "ORD-5431",
  timestamp: new Date().toISOString(),
  type: "COUNTER",
  paymentMethod: "upi",
  items: [
    { name: "Kaju Masala (काजू मसाला)", quantity: 1, price: 180 },
    { name: "Butter Naan (बटर नान)", quantity: 2, price: 40 },
    { name: "Jeera Rice (जिरा राईस)", quantity: 1, price: 110 },
    { name: "Masala Papad (मसाला पापड)", quantity: 2, price: 30 }
  ],
  subtotal: 430,
  tax: 21.5,
  discount: 30,
  total: 421.5
};

const getFormattedRestaurantData = (printType, lang) => {
  const trans = TRANSLATIONS[lang] || TRANSLATIONS.en;
  
  if (printType === 'coupon1') {
    const dObj = new Date(SAMPLE_COUPON1_DATA.timestamp);
    const dateStr = `${dObj.getDate()}/${dObj.getMonth() + 1}/${dObj.getFullYear()}`;
    const timeStr = dObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return {
      hotelName: "HOTEL MARDANI",
      branchName: "Branch Saidapur",
      couponNo: SAMPLE_COUPON1_DATA.coupon_number,
      date: dateStr,
      time: timeStr,
      items: SAMPLE_COUPON1_DATA.items.map(i => ({
        name: i.name,
        qtyRate: `${i.quantity} x ₹${i.price}`
      })),
      totalAmt: `${SAMPLE_COUPON1_DATA.total}`,
      footerVisit: "** Visit Again **",
      footerBrand: "powered by- DinexaPOS"
    };
  } else if (printType === 'coupon2') {
    const dObj = new Date(SAMPLE_COUPON2_DATA.timestamp);
    const dateStr = `${dObj.getDate()}/${dObj.getMonth() + 1}/${dObj.getFullYear()}`;
    const timeStr = dObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return {
      hotelName: "HOTEL MARDANI",
      branchName: "Branch Saidapur",
      couponNo: SAMPLE_COUPON2_DATA.coupon_number,
      date: dateStr,
      time: timeStr,
      items: SAMPLE_COUPON2_DATA.items.map(i => ({
        name: i.name,
        quantity: i.quantity,
        price: i.price,
        total: i.total
      })),
      totalAmt: `${SAMPLE_COUPON2_DATA.total}`,
      footerVisit: "** धन्यवाद! पुन्हा भेटूया! **",
      footerBrand: "Powered by Dinexa POS"
    };
  } else if (printType === 'kot') {
    return {
      header: trans.kotHeader,
      tableLabel: trans.table,
      tableValue: SAMPLE_KOT_DATA.tableNumber,
      idLabel: trans.kotNo,
      idValue: SAMPLE_KOT_DATA.id,
      timeLabel: trans.time,
      timeValue: new Date(SAMPLE_KOT_DATA.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      waiterLabel: trans.waiter,
      waiterValue: SAMPLE_KOT_DATA.waiterName,
      items: SAMPLE_KOT_DATA.items.map(item => ({
        name: item.name,
        quantity: item.quantity,
        remarks: item.remarks
      })),
      footer: trans.poweredBy
    };
  } else {
    return {
      header: trans.companyName,
      address: trans.address,
      tel: trans.tel,
      dateLabel: trans.date,
      dateValue: new Date(SAMPLE_BILL_DATA.timestamp).toLocaleDateString(),
      timeLabel: trans.time,
      timeValue: new Date(SAMPLE_BILL_DATA.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      tokenLabel: trans.token,
      tokenValue: SAMPLE_BILL_DATA.id.replace('ORD-', 'TKN-'),
      typeLabel: trans.type,
      typeValue: SAMPLE_BILL_DATA.type,
      paymentLabel: trans.payment,
      paymentValue: SAMPLE_BILL_DATA.paymentMethod.toUpperCase(),
      items: SAMPLE_BILL_DATA.items.map(item => ({
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        total: item.price * item.quantity
      })),
      subtotalLabel: trans.subtotal,
      subtotalValue: SAMPLE_BILL_DATA.subtotal,
      gstLabel: trans.gst,
      gstValue: SAMPLE_BILL_DATA.tax,
      discountLabel: trans.discount,
      discountValue: SAMPLE_BILL_DATA.discount,
      totalLabel: trans.totalPaid,
      totalValue: SAMPLE_BILL_DATA.total,
      footer: trans.thankYou
    };
  }
};

const PrinterUtility = () => {
  const { receiptTemplate, setReceiptTemplate, printFeedLines, setPrintFeedLines } = useApp();
  const [connectionType, setConnectionType] = useState(() => localStorage.getItem('dinexa_printer_connection_type') || 'system'); // system | usb | bluetooth | serial
  const [printType, setPrintType] = useState(() => {
    const raw = localStorage.getItem('react_pos_receipt_template') || localStorage.getItem('dinexa_receipt_template');
    try {
      if (raw) return raw.startsWith('"') ? JSON.parse(raw) : raw;
    } catch(e) {}
    return 'coupon1';
  });
  const [feedLines, setFeedLines] = useState(() => {
    const raw = localStorage.getItem('dinexa_print_feed_lines');
    return raw !== null && !isNaN(parseInt(raw, 10)) ? parseInt(raw, 10) : 3;
  });
  const [printersList, setPrintersList] = useState([]);
  const [selectedPrinterId, setSelectedPrinterId] = useState('default');
  const [pageSize, setPageSize] = useState(() => localStorage.getItem('dinexa_printer_page_size') || '58mm');
  const [directPrint, setDirectPrint] = useState(false);
  const [nativeTextPrint, setNativeTextPrint] = useState(() => localStorage.getItem('dinexa_printer_native_text') === 'true');

  // Custom styling settings
  const [fontSize, setFontSize] = useState(() => parseInt(localStorage.getItem('dinexa_printer_font_size') || '14', 10));
  const [receiptMargins, setReceiptMargins] = useState(() => parseInt(localStorage.getItem('dinexa_printer_margin_offset') || '0', 10));
  const [printableWidth, setPrintableWidth] = useState(() => parseInt(localStorage.getItem('dinexa_printer_content_width') || '300', 10));
  const [printerMemoryType, setPrinterMemoryType] = useState('low-memory'); // low-memory | high-memory
  const [printLanguage, setPrintLanguage] = useState(() => localStorage.getItem('dinexa_printer_lang') || 'mr'); // mr | en | hi
  const [printDensity, setPrintDensity] = useState(() => localStorage.getItem('dinexa_printer_density') || 'dark');

  // Real device reference states
  const [connectedDevices, setConnectedDevices] = useState([]);

  // Feedback states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // success | error | info | warning
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);

  const printFrameRef = useRef(null);

  useEffect(() => {
    if (printFeedLines !== undefined && printFeedLines !== null) {
      setFeedLines(printFeedLines);
    }
  }, [printFeedLines]);

  const handleTemplateChange = async (e) => {
    const val = typeof e === 'object' && e?.target ? e.target.value : e;
    setReceiptTemplate(val);
    setPrintType(val);
    localStorage.setItem('react_pos_receipt_template', JSON.stringify(val));
    localStorage.setItem('dinexa_receipt_template', val);

    try {
      ['react_pos_current_user', 'dinexa_current_user'].forEach(k => {
        const uStr = localStorage.getItem(k) || sessionStorage.getItem(k);
        if (uStr) {
          const uObj = JSON.parse(uStr);
          uObj.receipt_template = val;
          localStorage.setItem(k, JSON.stringify(uObj));
          sessionStorage.setItem(k, JSON.stringify(uObj));
        }
      });
    } catch(err) {}

    try {
      const activeSubdomain = localStorage.getItem('dinexa_active_tenant_subdomain') || 
                              localStorage.getItem('dinexa_tenant_subdomain') || 
                              window.location.hostname.split('.')[0];
      const res = await fetch(`${API_BASE_URL}/tenant/settings/printer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Subdomain': activeSubdomain
        },
        body: JSON.stringify({ 
          receipt_template: val,
          print_feed_lines: feedLines
        })
      });
      const data = await res.json();
      if(data.success) {
        triggerSnackbar(`✅ कूपन व पावती लेआउट (${val.toUpperCase()}) डेटाबेसमध्ये सेव्ह झाले!`, 'success');
      } else {
        triggerSnackbar(`⚠️ सर्व्हर त्रुटी: ${data.error || 'Failed to update template'}`, 'error');
      }
    } catch(err) {
      triggerSnackbar('⚠️ डेटाबेसशी संपर्क होऊ शकला नाही, स्थानिक मेमरीमध्ये सेव्ह केले.', 'warning');
    }
  };

  const handleFeedLinesChange = async (e) => {
    const val = parseInt(typeof e === 'object' && e?.target ? e.target.value : e, 10);
    setFeedLines(val);
    if (setPrintFeedLines) setPrintFeedLines(val);
    localStorage.setItem('dinexa_print_feed_lines', val.toString());

    try {
      ['react_pos_current_user', 'dinexa_current_user'].forEach(k => {
        const uStr = localStorage.getItem(k) || sessionStorage.getItem(k);
        if (uStr) {
          const uObj = JSON.parse(uStr);
          uObj.print_feed_lines = val;
          localStorage.setItem(k, JSON.stringify(uObj));
          sessionStorage.setItem(k, JSON.stringify(uObj));
        }
      });
    } catch(err) {}

    try {
      const activeSubdomain = localStorage.getItem('dinexa_active_tenant_subdomain') || 
                              localStorage.getItem('dinexa_tenant_subdomain') || 
                              window.location.hostname.split('.')[0];
      const res = await fetch(`${API_BASE_URL}/tenant/settings/printer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Subdomain': activeSubdomain
        },
        body: JSON.stringify({ 
          receipt_template: receiptTemplate,
          print_feed_lines: val
        })
      });
      const data = await res.json();
      if(data.success) {
        triggerSnackbar(`✅ पेपर लाईन फीड (${val} Lines) डेटाबेसमध्ये सेव्ह झाले!`, 'success');
      } else {
        triggerSnackbar(`⚠️ सर्व्हर त्रुटी: ${data.error || 'Failed to update feed lines'}`, 'error');
      }
    } catch(err) {
      triggerSnackbar('⚠️ डेटाबेसशी संपर्क होऊ शकला नाही, स्थानिक मेमरीमध्ये सेव्ह केले.', 'warning');
    }
  };

  const handleSaveAllSettings = async () => {
    localStorage.setItem('react_pos_receipt_template', JSON.stringify(receiptTemplate));
    localStorage.setItem('dinexa_receipt_template', receiptTemplate);
    localStorage.setItem('dinexa_print_feed_lines', feedLines.toString());
    localStorage.setItem('dinexa_printer_page_size', pageSize);
    localStorage.setItem('dinexa_printer_font_size', fontSize.toString());
    localStorage.setItem('dinexa_printer_native_text', nativeTextPrint ? 'true' : 'false');
    localStorage.setItem('dinexa_printer_density', printDensity);
    localStorage.setItem('dinexa_printer_connection_type', connectionType);
    localStorage.setItem('dinexa_printer_lang', printLanguage);

    try {
      ['react_pos_current_user', 'dinexa_current_user'].forEach(k => {
        const uStr = localStorage.getItem(k) || sessionStorage.getItem(k);
        if (uStr) {
          const uObj = JSON.parse(uStr);
          uObj.receipt_template = receiptTemplate;
          uObj.print_feed_lines = feedLines;
          localStorage.setItem(k, JSON.stringify(uObj));
          sessionStorage.setItem(k, JSON.stringify(uObj));
        }
      });
    } catch(err) {}

    try {
      const activeSubdomain = localStorage.getItem('dinexa_active_tenant_subdomain') || 
                              localStorage.getItem('dinexa_tenant_subdomain') || 
                              window.location.hostname.split('.')[0];
      const res = await fetch(`${API_BASE_URL}/tenant/settings/printer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Subdomain': activeSubdomain
        },
        body: JSON.stringify({ 
          receipt_template: receiptTemplate,
          print_feed_lines: feedLines
        })
      });
      const data = await res.json();
      if(data.success) {
        triggerSnackbar(`✅ सर्व प्रिंटर, कूपन (${receiptTemplate.toUpperCase()}) व लाईन फीड (${feedLines}) सेटिंग्ज डेटाबेसमध्ये यशस्वीरित्या सेव्ह झाल्या!`, 'success');
      } else {
        triggerSnackbar(`⚠️ सर्व्हर त्रुटी: ${data.error || 'सेटिंग्ज सेव्ह अयशस्वी'}`, 'error');
      }
    } catch(err) {
      triggerSnackbar('⚠️ डेटाबेसशी संपर्क होऊ शकला नाही, स्थानिक मेमरीमध्ये सेव्ह केले.', 'warning');
    }
  };

  // Sync settings with localStorage
  useEffect(() => {
    localStorage.setItem('dinexa_print_feed_lines', feedLines.toString());
  }, [feedLines]);

  useEffect(() => {
    localStorage.setItem('dinexa_printer_page_size', pageSize);
  }, [pageSize]);

  useEffect(() => {
    localStorage.setItem('dinexa_printer_font_size', fontSize.toString());
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem('dinexa_printer_native_text', nativeTextPrint ? 'true' : 'false');
  }, [nativeTextPrint]);

  useEffect(() => {
    localStorage.setItem('dinexa_printer_density', printDensity);
  }, [printDensity]);

  useEffect(() => {
    localStorage.setItem('dinexa_printer_connection_type', connectionType);
  }, [connectionType]);

  useEffect(() => {
    localStorage.setItem('dinexa_printer_lang', printLanguage);
  }, [printLanguage]);

  // Load previously paired Web Bluetooth devices on mount
  useEffect(() => {
    const initDevices = async () => {
      try {
        if ('bluetooth' in navigator) {
          const devices = await navigator.bluetooth.getDevices();
          const loaded = devices.map(device => {
            const devId = `bt-${device.id}`;
            const devName = device.name || 'Generic Bluetooth Printer';
            return { id: devId, name: devName, type: 'bluetooth', rawDevice: device };
          });
          if (loaded.length > 0) {
            setConnectedDevices(prev => {
              const filtered = prev.filter(d => d.type !== 'bluetooth');
              return [...filtered, ...loaded];
            });
            const savedName = localStorage.getItem('dinexa_printer_device_name');
            const match = loaded.find(d => d.name === savedName);
            if (match) {
              setSelectedPrinterId(match.id);
            } else {
              setSelectedPrinterId(loaded[0].id);
            }
          }
        }
      } catch (e) {
        console.error("Error fetching paired Bluetooth devices:", e);
      }
    };
    initDevices();
  }, []);

  // Check URL parameters to see if troubleshooting guide is requested
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('help') === 'true' || urlParams.get('showHelp') === 'true') {
      setShowTroubleshooting(true);
    }
  }, []);

  // Update printers dropdown list when connected devices list updates
  useEffect(() => {
    const defaultList = [{ id: 'default', name: 'Default Printer (System Direct)' }];
    const devicesList = connectedDevices.map(d => ({ id: d.id, name: `${d.name} (${d.type.toUpperCase()})` }));
    setPrintersList([...defaultList, ...devicesList]);
  }, [connectedDevices]);

  const triggerSnackbar = (msg, severity = 'success') => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Triggers the Web API selection scanner depending on mode
  const handlePairPrinter = async () => {
    try {
      if (connectionType === 'usb') {
        if (!('usb' in navigator)) {
          triggerSnackbar('WebUSB is not supported in this browser. Please use Chrome/Edge.', 'error');
          return;
        }
        const device = await navigator.usb.requestDevice({ filters: [] });
        const devId = `usb-${device.vendorId}-${device.productId}-${device.serialNumber || Math.random().toString(36).substr(2, 4)}`;
        const devName = device.productName || `USB Device (${device.vendorId}:${device.productId})`;

        if (!connectedDevices.some(d => d.id === devId)) {
          setConnectedDevices(prev => [...prev, { id: devId, name: devName, type: 'usb', rawDevice: device }]);
        }
        setSelectedPrinterId(devId);
        triggerSnackbar(`Successfully paired USB device: ${devName}`, 'success');
      } else if (connectionType === 'bluetooth') {
        if (!('bluetooth' in navigator)) {
          triggerSnackbar('Web Bluetooth is not supported in this browser. Please use Chrome/Edge.', 'error');
          return;
        }
        
        const devName = await BluetoothPrinterManager.connect();
        const devId = `bt-${BluetoothPrinterManager.device.id}`;

        if (!connectedDevices.some(d => d.id === devId)) {
          setConnectedDevices(prev => [...prev, { id: devId, name: devName, type: 'bluetooth', rawDevice: BluetoothPrinterManager.device }]);
        }
        setSelectedPrinterId(devId);
        triggerSnackbar(`Successfully paired Bluetooth device: ${devName}`, 'success');
      } else if (connectionType === 'serial') {
        if (!('serial' in navigator)) {
          triggerSnackbar('Web Serial API is not supported in this browser. Please use Chrome/Edge.', 'error');
          return;
        }
        const port = await navigator.serial.requestPort();
        const devId = `serial-${Math.random().toString(36).substr(2, 9)}`;
        const devName = `COM Port / Serial Printer`;

        if (!connectedDevices.some(d => d.id === devId)) {
          setConnectedDevices(prev => [...prev, { id: devId, name: devName, type: 'serial', rawDevice: port }]);
        }
        setSelectedPrinterId(devId);
        triggerSnackbar(`Successfully connected Serial/COM Port!`, 'success');
      }
    } catch (err) {
      if (err.name !== 'NotFoundError') {
        triggerSnackbar(`Connection failed: ${err.message}`, 'error');
      }
    }
  };

  // HTML print contents generator for Browser print
  const getPrintHTML = () => {
    const is80 = pageSize === '80mm';
    const sizeStr = is80 ? '70mm' : '46mm';
    const pageStr = is80 ? '72mm auto' : '46mm auto';

    if (printType === 'coupon1') {
      const d = getFormattedRestaurantData('coupon1', printLanguage);
      return `
        <html>
          <head>
            <style>
              @page { size: ${pageStr}; margin: 0 !important; }
              html, body {
                font-family: 'Noto Sans Devanagari', 'Courier New', monospace;
                width: ${sizeStr};
                max-width: ${sizeStr};
                box-sizing: border-box;
                margin: 0 !important;
                padding: 0 !important;
                font-size: ${fontSize}px;
                line-height: 1.15;
                color: #000;
                overflow: hidden;
                height: auto !important;
                min-height: 0 !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 3px; width: 100%; }
              .hotel-box { flex: 1; text-align: left; max-width: calc(100% - 58px); word-break: break-word; }
              .hotel-name { font-size: 1.05em; font-weight: 900; line-height: 1.1; }
              .branch-name { font-size: 0.75em; font-weight: bold; margin-top: 1px; }
              .coupon-box {
                border: 1.5px solid #000;
                padding: 2px 3px;
                text-align: center;
                min-width: 48px;
                max-width: 54px;
                box-sizing: border-box;
              }
              .coupon-label { font-size: 0.7em; font-weight: bold; }
              .coupon-number { font-size: 1.4em; font-weight: 900; line-height: 1; }
              .datetime-row { display: flex; justify-content: space-between; font-size: 0.8em; font-weight: bold; margin-bottom: 2px; }
              .dashed { border-bottom: 1.2px dashed #000; margin: 3px 0; }
              .item-header { display: flex; justify-content: space-between; font-weight: 900; font-size: 0.85em; }
              .item-row { display: flex; justify-content: space-between; font-weight: bold; font-size: 0.85em; padding: 1.5px 0; }
              .total-row { display: flex; justify-content: space-between; font-weight: 900; font-size: 1.1em; padding: 1.5px 0; }
              .center { text-align: center; }
              .footer-top { font-weight: bold; font-size: 0.8em; margin-top: 3px; margin-bottom: 0px; }
              .footer-bottom { font-weight: 900; font-size: 0.75em; margin-top: 1px; margin-bottom: 0px !important; padding-bottom: 0px !important; line-height: 1; }
            </style>
          </head>
          <body>
            <div class="header-row">
              <div class="hotel-box">
                <div class="hotel-name">${d.hotelName}</div>
                <div class="branch-name">${d.branchName}</div>
              </div>
              <div class="coupon-box">
                <div class="coupon-label">Coupon No.</div>
                <div class="coupon-number">${d.couponNo}</div>
              </div>
            </div>
            <div class="datetime-row">
              <span>दिनांक: ${d.date}</span>
              <span>${d.time}</span>
            </div>
            <div class="dashed"></div>
            <div class="item-header">
              <span>पदार्थ</span>
              <span>Qty * Rate</span>
            </div>
            ${d.items.map(item => `
              <div class="item-row">
                <span>${item.name}</span>
                <span>${item.qtyRate}</span>
              </div>
            `).join('')}
            <div class="dashed"></div>
            <div class="total-row">
              <span>एकूण बिल रक्कम</span>
              <span>${d.totalAmt}</span>
            </div>
            <div class="dashed"></div>
            <div class="center footer-top">${d.footerVisit}</div>
            <div class="center footer-bottom">${d.footerBrand}</div>
            ${feedLines > 0 ? `<div style="height: ${feedLines * 12}px; width: 100%; margin: 0; padding: 0;"></div>` : ''}
          </body>
        </html>
      `;
    } else if (printType === 'coupon2') {
      const d = getFormattedRestaurantData('coupon2', printLanguage);
      return `
        <html>
          <head>
            <style>
              @page { size: ${pageStr}; margin: 0 !important; }
              body {
                font-family: 'Noto Sans Devanagari', 'Courier New', monospace;
                width: ${sizeStr};
                max-width: ${sizeStr};
                box-sizing: border-box;
                margin: 0 !important;
                padding: 0 !important;
                font-size: ${fontSize}px;
                color: #000;
                overflow: hidden;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .center { text-align: center; }
              .token-badge { background: #ffffff; color: #000000; border: 1.5px solid #000000; padding: 4px 8px; font-weight: 900; font-size: 1.15em; text-align: center; border-radius: 4px; margin-bottom: 4px; }
              .hotel-name { font-size: 1.05em; font-weight: 900; }
              .branch-name { font-size: 0.75em; font-weight: bold; margin-bottom: 3px; }
              .datetime-row { display: flex; justify-content: space-between; font-size: 0.8em; font-weight: bold; margin-bottom: 3px; }
              .dashed { border-bottom: 1.2px dashed #000; margin: 4px 0; }
              .item-header { display: flex; justify-content: space-between; font-weight: 900; font-size: 0.85em; }
              .item-row { display: flex; justify-content: space-between; font-weight: bold; font-size: 0.85em; padding: 2px 0; }
              .total-row { display: flex; justify-content: space-between; font-weight: 900; font-size: 1.1em; padding: 2px 0; }
              .footer-top { font-weight: bold; font-size: 0.8em; margin-top: 4px; }
              .footer-bottom { font-weight: 800; font-size: 0.75em; margin-top: 1px; }
            </style>
          </head>
          <body>
            <div class="token-badge">COUPON #${d.couponNo}</div>
            <div class="center hotel-name">${d.hotelName}</div>
            ${d.branchName ? `<div class="center branch-name">Branch: ${d.branchName}</div>` : ''}
            <div class="datetime-row">
              <span>दिनांक: ${d.date}</span>
              <span>${d.time}</span>
            </div>
            <div class="dashed"></div>
            <div class="item-header">
              <span>पदार्थ</span>
              <span>नग x दर</span>
            </div>
            ${d.items.map(item => `
              <div class="item-row">
                <span>${item.name}</span>
                <span>${item.quantity} x ₹${item.price}</span>
              </div>
            `).join('')}
            <div class="dashed"></div>
            <div class="total-row">
              <span>एकूण रक्कम :</span>
              <span>₹${d.totalAmt}</span>
            </div>
            <div class="dashed"></div>
            <div class="center footer-top">${d.footerVisit}</div>
            <div class="center footer-bottom">${d.footerBrand}</div>
            ${feedLines > 0 ? `<div style="height: ${feedLines * 12}px; width: 100%; margin: 0; padding: 0;"></div>` : ''}
          </body>
        </html>
      `;
    } else if (printType === 'kot') {
      const d = getFormattedRestaurantData('kot', printLanguage);
      return `
        <html>
          <head>
            <style>
              @page {
                size: ${pageStr};
                margin: 0 !important;
              }
              body { 
                font-family: 'Courier New', Courier, monospace; 
                width: ${sizeStr}; 
                max-width: ${sizeStr};
                box-sizing: border-box;
                margin: 0 !important;
                padding: 0 !important; 
                font-size: ${fontSize}px;
                line-height: 1.3;
                color: #000;
                overflow: hidden;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .center { text-align: center; }
              .bold { font-weight: bold; }
              .header { font-size: 1.2em; margin-bottom: 4px; color: #ef4444; border-bottom: 1.5px dashed #ef4444; padding-bottom: 3px; }
              .table-block { font-size: 1.25em; background-color: #ef4444; color: #fff; padding: 3px; text-align: center; margin: 4px 0; font-weight: bold; }
              .indent-row {
                display: flex;
                margin-bottom: 3px;
                justify-content: space-between;
              }
              .item-header {
                display: flex;
                justify-content: space-between;
                font-weight: bold;
                border-bottom: 1px dashed #000;
                margin-top: 6px;
                padding-bottom: 3px;
              }
              .item-row {
                padding: 3px 0;
                border-bottom: 1px dashed #ccc;
              }
              .item-main {
                display: flex;
                justify-content: space-between;
                font-weight: bold;
              }
              .remarks {
                font-size: 0.85em;
                color: #ef4444;
                font-style: italic;
                padding-left: 6px;
              }
            </style>
          </head>
          <body>
            <div class="center bold header">${d.header}</div>
            <div class="table-block">${d.tableLabel} ${d.tableValue}</div>
            <div class="indent-row">
              <span>${d.idLabel}: ${d.idValue}</span>
              <span>${d.timeLabel}: ${d.timeValue}</span>
            </div>
            <div class="indent-row">
              <span>${d.waiterLabel}: ${d.waiterValue}</span>
            </div>
            <div class="item-header">
              <span>Item (पदार्थ)</span>
              <span style="text-align: right;">Qty</span>
            </div>
            ${d.items.map(item => `
              <div class="item-row">
                <div class="item-main">
                  <span>${item.name}</span>
                  <span>x ${item.quantity}</span>
                </div>
                ${item.remarks ? `<div class="remarks">* Instruction: ${item.remarks}</div>` : ''}
              </div>
            `).join('')}
            <div style="margin-top: 10px; text-align: center; font-size: 0.8em; color: #666;">
              ${d.footer}
            </div>
            ${feedLines > 0 ? `<div style="height: ${feedLines * 12}px; width: 100%; margin: 0; padding: 0;"></div>` : ''}
          </body>
        </html>
      `;
    } else {
      const d = getFormattedRestaurantData('counter', printLanguage);
      const separatorLine = pageSize === '58mm' ? '============================' : '========================================';
      return `
        <html>
          <head>
            <style>
              @page {
                size: ${pageStr};
                margin: 0 !important;
              }
              body { 
                font-family: 'Courier New', Courier, monospace; 
                width: ${sizeStr}; 
                max-width: ${sizeStr};
                box-sizing: border-box;
                margin: 0 !important;
                padding: 0 !important; 
                font-size: ${fontSize}px;
                line-height: 1.3;
                color: #000;
                overflow: hidden;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .center { text-align: center; }
              .bold { font-weight: bold; }
              .header { font-size: 1.15em; margin-bottom: 2px; }
              .subheader { font-size: 0.85em; margin-bottom: 4px; }
              .indent-row {
                display: flex;
                margin-bottom: 3px;
                justify-content: space-between;
              }
              .item-header {
                display: flex;
                justify-content: space-between;
                font-weight: bold;
                border-bottom: 1px dashed #000;
                margin-top: 6px;
                padding-bottom: 3px;
              }
              .item-row {
                display: flex;
                justify-content: space-between;
                padding: 3px 0;
              }
              .totals-box {
                margin-top: 6px;
                border-top: 1px dashed #000;
                padding-top: 3px;
              }
              .total-row {
                display: flex;
                justify-content: space-between;
                padding: 2px 0;
              }
              .final-total {
                font-weight: bold;
                font-size: 1.05em;
                border-top: 1px solid #000;
                padding-top: 3px;
                margin-top: 3px;
              }
            </style>
          </head>
          <body>
            <div class="center bold header">${d.header}</div>
            <div class="center subheader">${d.address}<br/>${d.tel}</div>
            <div style="border-bottom: 1px dashed #000; margin-bottom: 4px;"></div>
            <div class="indent-row">
              <span>${d.dateLabel}: ${d.dateValue}</span>
              <span>${d.timeLabel}: ${d.timeValue}</span>
            </div>
            <div class="indent-row bold">
              <span>${d.tokenLabel}: ${d.tokenValue}</span>
            </div>
            <div class="indent-row">
              <span>${d.typeLabel}: ${d.typeValue}</span>
              <span>${d.paymentLabel}: ${d.paymentValue}</span>
            </div>
            <div class="item-header">
              <span style="flex-grow: 1;">Item</span>
              <span style="width: 35px; text-align: center;">Qty</span>
              <span style="width: 55px; text-align: right;">Amt</span>
            </div>
            ${d.items.map(item => `
              <div class="item-row">
                <span style="flex-grow: 1;">${item.name}</span>
                <span style="width: 35px; text-align: center;">${item.quantity}</span>
                <span style="width: 55px; text-align: right;">₹${item.total.toFixed(0)}</span>
              </div>
            `).join('')}
            <div class="totals-box">
              <div class="total-row">
                <span>${d.subtotalLabel}</span>
                <span>₹${d.subtotalValue.toFixed(0)}</span>
              </div>
              <div class="total-row">
                <span>${d.gstLabel}</span>
                <span>₹${d.gstValue.toFixed(0)}</span>
              </div>
              ${d.discountValue > 0 ? `
                <div class="total-row" style="color: red;">
                  <span>${d.discountLabel}</span>
                  <span>-₹${d.discountValue.toFixed(0)}</span>
                </div>
              ` : ''}
              <div class="total-row final-total">
                <span>${d.totalLabel}</span>
                <span>₹${d.totalValue.toFixed(0)}</span>
              </div>
            <div style="border-top: 1px dashed #000; margin-top: 8px; padding-top: 6px; text-align: center; font-weight: bold;">
              ${d.footer}
            </div>
            <div class="center" style="font-size: 0.75em; color: #666; margin-top: 2px;">Powered by Dinexa POS</div>
            ${feedLines > 0 ? `<div style="height: ${feedLines * 12}px; width: 100%; margin: 0; padding: 0;"></div>` : ''}
          </body>
        </html>
      `;
    }
  };

  // Actual System Print execution
  const executeSystemPrint = () => {
    const iframe = printFrameRef.current;
    if (!iframe) return;

    const printDoc = iframe.contentDocument || iframe.contentWindow.document;
    printDoc.open();
    printDoc.write(getPrintHTML());
    printDoc.close();

    // Trigger iframe window printing
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 250);
  };

  // Generate 1-bit raster bitmap from HTML canvas
  // Generate 1-bit raster bitmap from HTML canvas
  const generateEscPosCanvasBytes = (pageSize) => {
    const is80mm = pageSize === '80mm';
    const width = is80mm ? 576 : 384;

    // Calculate dynamic scaling based on selected fontSize (normalized to base 14px)
    const scale = (fontSize >= 10 && fontSize <= 20) ? fontSize / 14 : 1.0;

    // Use a temporary scratch canvas of high height to draw the items dynamically
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = 2500; // tall enough for any receipt/KOT

    const ctx = tempCanvas.getContext('2d');

    // Fill white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

    // Draw in black
    ctx.fillStyle = '#000000';

    const marginOffset = typeof receiptMargins === 'number' ? Math.max(0, receiptMargins) : 0;
    const contentW = is80mm ? 536 : (typeof printableWidth === 'number' ? printableWidth : 300);
    const L_MARGIN = is80mm ? 16 : marginOffset;
    const R_BOUND = is80mm ? width - 20 : Math.min(360, L_MARGIN + contentW);
    const C_X = (L_MARGIN + R_BOUND) / 2;

    let y = Math.round(24 * scale);

    const drawTextCenter = (text, fontSizePx, fontStyle = '') => {
      ctx.font = `${fontStyle} ${fontSizePx}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(text, C_X, y);
    };

    const drawTextRow = (leftText, rightText, fontSizePx, fontStyle = '') => {
      ctx.font = `${fontStyle} ${fontSizePx}px Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.fillText(leftText, L_MARGIN, y);
      ctx.textAlign = 'right';
      ctx.fillText(rightText, R_BOUND, y);
    };

    const drawSeparator = () => {
      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.moveTo(L_MARGIN, y - Math.round(6 * scale));
      ctx.lineTo(R_BOUND, y - Math.round(6 * scale));
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.setLineDash([]);
    };

    if (printType === 'coupon1') {
      const data = getFormattedRestaurantData('coupon1', printLanguage);
      const fontDev = `'Noto Sans Devanagari', 'Mukta', 'Devanagari MT', sans-serif`;

      // 1. Top Header Box & Hotel Name
      const boxWidth = Math.round((is80mm ? 90 : 58) * scale);
      const boxHeight = Math.round((is80mm ? 54 : 40) * scale);
      const boxX = R_BOUND - boxWidth;
      const boxY = y - Math.round(14 * scale);

      ctx.lineWidth = 1.5;
      ctx.strokeStyle = '#000000';
      ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

      ctx.font = `bold ${Math.round(10 * scale)}px ${fontDev}`;
      ctx.textAlign = 'center';
      ctx.fillText("Coupon No.", boxX + (boxWidth / 2), boxY + Math.round(13 * scale));

      ctx.font = `900 ${Math.round(21 * scale)}px ${fontDev}`;
      ctx.textAlign = 'center';
      ctx.fillText(String(data.couponNo), boxX + (boxWidth / 2), boxY + Math.round(34 * scale));

      // Draw Left Hotel Name & Branch
      const leftMaxWidth = boxX - L_MARGIN - 6;
      let leftY = y;

      ctx.font = `900 ${Math.round(15 * scale)}px ${fontDev}`;
      ctx.textAlign = 'left';
      const words = (data.hotelName || '').toUpperCase().split(' ');
      let line = '';
      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        if (ctx.measureText(testLine).width > leftMaxWidth && n > 0) {
          ctx.fillText(line.trim(), L_MARGIN, leftY);
          line = words[n] + ' ';
          leftY += Math.round(19 * scale);
        } else {
          line = testLine;
        }
      }
      if (line.trim()) {
        ctx.fillText(line.trim(), L_MARGIN, leftY);
        leftY += Math.round(15 * scale);
      }

      if (data.branchName) {
        ctx.font = `bold ${Math.round(12 * scale)}px ${fontDev}`;
        const branchWords = (data.branchName || '').split(' ');
        let bLine = '';
        for (let bn = 0; bn < branchWords.length; bn++) {
          const testBLine = bLine + branchWords[bn] + ' ';
          if (ctx.measureText(testBLine).width > leftMaxWidth && bn > 0) {
            ctx.fillText(bLine.trim(), L_MARGIN, leftY);
            bLine = branchWords[bn] + ' ';
            leftY += Math.round(15 * scale);
          } else {
            bLine = testBLine;
          }
        }
        if (bLine.trim()) {
          ctx.fillText(bLine.trim(), L_MARGIN, leftY);
          leftY += Math.round(15 * scale);
        }
      }

      y = Math.max(boxY + boxHeight + Math.round(14 * scale), leftY + Math.round(12 * scale));

      // 2. Date & Time
      drawTextRow(`दिनांक: ${data.date}`, data.time, Math.round(13 * scale), 'bold');
      y += Math.round(18 * scale);

      // 3. Dashed separator
      drawSeparator();
      y += Math.round(16 * scale);

      // 4. Columns: पदार्थ | Qty * Rate
      drawTextRow("पदार्थ", "Qty * Rate", Math.round(13 * scale), '900');
      y += Math.round(18 * scale);

      data.items.forEach(item => {
        ctx.font = `bold ${Math.round(13.5 * scale)}px ${fontDev}`;
        ctx.textAlign = 'left';
        
        const maxNameWidth = is80mm ? 300 : 155;
        let nameToPrint = item.name;
        if (ctx.measureText(nameToPrint).width > maxNameWidth) {
          nameToPrint = nameToPrint.substring(0, 13) + '..';
        }
        ctx.fillText(nameToPrint, L_MARGIN, y);
        ctx.textAlign = 'right';
        ctx.fillText(item.qtyRate, R_BOUND, y);
        y += Math.round(19 * scale);
      });

      // 5. Dashed separator
      drawSeparator();
      y += Math.round(18 * scale);

      // 6. Total Amount
      ctx.font = `900 ${Math.round(14 * scale)}px ${fontDev}`;
      ctx.textAlign = 'left';
      ctx.fillText("एकूण बिल रक्कम", L_MARGIN, y);
      ctx.textAlign = 'right';
      ctx.font = `900 ${Math.round(18 * scale)}px ${fontDev}`;
      ctx.fillText(data.totalAmt, R_BOUND, y);
      y += Math.round(22 * scale);

      // 7. Dashed separator
      drawSeparator();
      y += Math.round(16 * scale);

      // 8. Footer
      drawTextCenter(data.footerVisit, Math.round(12 * scale), 'bold');
      y += Math.round(16 * scale);
      drawTextCenter(data.footerBrand, Math.round(11 * scale), '800');
      y += Math.round(6 * scale);

    } else if (printType === 'coupon2') {
      const data = getFormattedRestaurantData('coupon2', printLanguage);
      const fontDev = `'Noto Sans Devanagari', 'Mukta', 'Devanagari MT', sans-serif`;

      // 1. Centered Large Token Header Badge
      const badgeW = R_BOUND - L_MARGIN;
      const badgeH = Math.round(36 * scale);
      const badgeX = L_MARGIN;
      const badgeY = y - Math.round(14 * scale);

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(badgeX, badgeY, badgeW, badgeH);

      ctx.lineWidth = 1.5;
      ctx.strokeStyle = '#000000';
      ctx.strokeRect(badgeX, badgeY, badgeW, badgeH);

      ctx.fillStyle = '#000000';
      ctx.font = `900 ${Math.round(18 * scale)}px ${fontDev}`;
      ctx.textAlign = 'center';
      ctx.fillText(`COUPON  #${data.couponNo}`, C_X, y + Math.round(10 * scale));

      y += Math.round(34 * scale);

      // 2. Hotel Name & Branch
      drawTextCenter(data.hotelName.toUpperCase(), Math.round(15 * scale), '900');
      y += Math.round(18 * scale);
      if (data.branchName) {
        drawTextCenter(`Branch: ${data.branchName}`, Math.round(12 * scale), 'bold');
        y += Math.round(15 * scale);
      }

      // 3. Date & Time
      drawTextRow(`दिनांक: ${data.date}`, data.time, Math.round(13 * scale), 'bold');
      y += Math.round(18 * scale);

      drawSeparator();
      y += Math.round(16 * scale);

      // 4. Columns: पदार्थ | नग x दर
      drawTextRow("पदार्थ", "नग x दर", Math.round(13 * scale), '900');
      y += Math.round(18 * scale);

      data.items.forEach(item => {
        ctx.font = `bold ${Math.round(13.5 * scale)}px ${fontDev}`;
        ctx.textAlign = 'left';
        const maxNameWidth = is80mm ? 300 : 175;
        let nameToPrint = item.name;
        if (ctx.measureText(nameToPrint).width > maxNameWidth) {
          nameToPrint = nameToPrint.substring(0, 14) + '..';
        }
        ctx.fillText(nameToPrint, L_MARGIN, y);
        ctx.textAlign = 'right';
        ctx.fillText(`${item.quantity} x ₹${item.price}`, R_BOUND, y);
        y += Math.round(19 * scale);
      });

      drawSeparator();
      y += Math.round(18 * scale);

      // 5. Total
      drawTextRow("एकूण रक्कम :", `₹${data.totalAmt}`, Math.round(14 * scale), '900');
      y += Math.round(22 * scale);

      drawSeparator();
      y += Math.round(16 * scale);

      // 6. Footer
      drawTextCenter(data.footerVisit, Math.round(12 * scale), 'bold');
      y += Math.round(16 * scale);
      drawTextCenter(data.footerBrand, Math.round(11 * scale), '800');
      y += Math.round(6 * scale);

    } else if (printType === 'kot') {
      const data = getFormattedRestaurantData('kot', printLanguage);

      drawTextCenter(data.header, Math.round(18 * scale), 'bold');
      y += Math.round(24 * scale);

      ctx.fillStyle = '#000000';
      ctx.fillRect(L_MARGIN, y - Math.round(16 * scale), R_BOUND - L_MARGIN, Math.round(28 * scale));
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${Math.round(16 * scale)}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(`${data.tableLabel} ${data.tableValue}`, C_X, y + Math.round(4 * scale));
      ctx.fillStyle = '#000000';
      y += Math.round(26 * scale);

      drawTextRow(`${data.idLabel}: ${data.idValue}`, `${data.timeLabel}: ${data.timeValue}`, Math.round(13 * scale));
      y += Math.round(18 * scale);
      drawTextRow(`${data.waiterLabel}: ${data.waiterValue}`, "", Math.round(13 * scale));
      y += Math.round(18 * scale);

      drawSeparator();
      y += Math.round(16 * scale);

      drawTextRow("Item (पदार्थ)", "Qty", Math.round(13 * scale), 'bold');
      y += Math.round(18 * scale);
      drawSeparator();
      y += Math.round(18 * scale);

      data.items.forEach(item => {
        ctx.font = `bold ${Math.round(13.5 * scale)}px Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.fillText(item.name, L_MARGIN, y);
        ctx.textAlign = 'right';
        ctx.fillText(`x ${item.quantity}`, R_BOUND, y);
        y += Math.round(18 * scale);
        if (item.remarks) {
          ctx.font = `italic ${Math.round(12 * scale)}px Arial, sans-serif`;
          ctx.textAlign = 'left';
          ctx.fillText(`* Instruction: ${item.remarks}`, L_MARGIN + 10, y);
          y += Math.round(16 * scale);
        }
        y += Math.round(6 * scale);
      });

      drawSeparator();
      y += Math.round(18 * scale);
      drawTextCenter(data.footer, Math.round(11 * scale), '800');
      y += Math.round(6 * scale);

    } else {
      const data = getFormattedRestaurantData('counter', printLanguage);

      // Header
      drawTextCenter(data.header, Math.round(18 * scale), 'bold');
      y += Math.round(22 * scale);

      // Address & Tel
      drawTextCenter(data.address, Math.round(12 * scale));
      y += Math.round(16 * scale);
      drawTextCenter(data.tel, Math.round(12 * scale));
      y += Math.round(18 * scale);

      drawSeparator();
      y += Math.round(16 * scale);

      // Metadata
      drawTextRow(`${data.dateLabel}: ${data.dateValue}`, `${data.timeLabel}: ${data.timeValue}`, Math.round(13 * scale));
      y += Math.round(18 * scale);

      drawTextRow(`${data.tokenLabel}: ${data.tokenValue}`, "", Math.round(13 * scale), 'bold');
      y += Math.round(18 * scale);

      drawTextRow(`${data.typeLabel}: ${data.typeValue}`, `${data.paymentLabel}: ${data.paymentValue}`, Math.round(13 * scale));
      y += Math.round(18 * scale);

      drawSeparator();
      y += Math.round(16 * scale);

      // Items Header
      drawTextRow("Item", "Qty * Amt", Math.round(13 * scale), 'bold');
      y += Math.round(18 * scale);
      drawSeparator();
      y += Math.round(16 * scale);

      // Items List
      data.items.forEach(item => {
        ctx.font = `bold ${Math.round(13.5 * scale)}px Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.fillText(item.name, L_MARGIN, y);
        ctx.textAlign = 'right';
        ctx.fillText(`${item.quantity} x ₹${(item.total / item.quantity).toFixed(0)}`, R_BOUND, y);
        y += Math.round(19 * scale);
      });

      drawSeparator();
      y += Math.round(16 * scale);

      // Financial totals
      drawTextRow(data.subtotalLabel, `₹${data.subtotalValue.toFixed(0)}`, Math.round(13 * scale));
      y += Math.round(18 * scale);

      drawTextRow(data.gstLabel, `₹${data.gstValue.toFixed(0)}`, Math.round(13 * scale));
      y += Math.round(18 * scale);

      if (data.discountValue > 0) {
        drawTextRow(data.discountLabel, `-₹${data.discountValue.toFixed(0)}`, Math.round(13 * scale));
        y += Math.round(18 * scale);
      }

      drawTextRow(data.totalLabel, `₹${data.totalValue.toFixed(0)}`, Math.round(16 * scale), 'bold');
      y += Math.round(22 * scale);

      drawSeparator();
      y += Math.round(16 * scale);

      // Footer
      drawTextCenter(data.footer, Math.round(12 * scale), 'bold');
      y += Math.round(16 * scale);
      drawTextCenter("Powered by Dinexa POS", Math.round(11 * scale), '800');
      y += Math.round(6 * scale);
    }

    const finalHeight = y;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = finalHeight;
    const finalCtx = canvas.getContext('2d');
    finalCtx.drawImage(tempCanvas, 0, 0, width, finalHeight, 0, 0, width, finalHeight);

    // Process image data to ESC/POS 1-bit raster format
    const imgData = finalCtx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imgData.data;
    const widthBytes = canvas.width / 8;
    const bitmap = [];

    for (let yByteIndex = 0; yByteIndex < canvas.height; yByteIndex++) {
      for (let xByte = 0; xByte < widthBytes; xByte++) {
        let byteVal = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = xByte * 8 + bit;
          const idx = (yByteIndex * canvas.width + x) * 4;
          const r = pixels[idx];
          const g = pixels[idx + 1];
          const b = pixels[idx + 2];
          const a = pixels[idx + 3];

          let isBlack = 0;
          if (a > 50) { // Not fully transparent
            const brightness = (r + g + b) / 3;
            const settings = BluetoothPrinterManager.getSettings();
            if (brightness < settings.threshold) {
              isBlack = 1;
            }
          }
          if (isBlack) {
            byteVal |= (1 << (7 - bit));
          }
        }
        bitmap.push(byteVal);
      }
    }

    // Build ESC/POS command list
    const ESC = 0x1B;
    const GS = 0x1D;
    const cmds = [];

    // Line spacing 0
    cmds.push(ESC, 0x33, 0x00);
    cmds.push(GS, 0x76, 0x30, 0x00);
    cmds.push(widthBytes % 256, Math.floor(widthBytes / 256));
    cmds.push(canvas.height % 256, Math.floor(canvas.height / 256));
    cmds.push(...bitmap);
    
    // Clean paper feed strictly based on feedLines (0 lines sends 0 bytes)
    const feedCount = typeof feedLines === 'number' ? Math.max(0, Math.min(15, feedLines)) : 3;
    cmds.push(ESC, 0x32);
    if (feedCount > 0) {
      for (let i = 0; i < feedCount; i++) {
        cmds.push(0x0A);
      }
    }

    return new Uint8Array(cmds);
  };

  const generateEscPosTextBytes = (pageSize) => {
    const ESC = 0x1B;
    const GS = 0x1D;
    const cmds = [];

    const textToBytes = (text) => {
      const bytes = [];
      for (let i = 0; i < text.length; i++) {
        bytes.push(text.charCodeAt(i));
      }
      return bytes;
    };

    const addText = (text) => cmds.push(...textToBytes(text));
    const addLine = (text) => { addText(text); cmds.push(0x0A); };
    const alignCenter = () => cmds.push(ESC, 0x61, 1);
    const alignLeft = () => cmds.push(ESC, 0x61, 0);
    const alignRight = () => cmds.push(ESC, 0x61, 2);
    const boldOn = () => cmds.push(ESC, 0x45, 1);
    const boldOff = () => cmds.push(ESC, 0x45, 0);
    const sizeNormal = () => cmds.push(GS, 0x21, 0x00);
    const sizeLarge = () => cmds.push(GS, 0x21, 0x11);
    const separator = pageSize === '80mm' ? '------------------------------------------------' : '--------------------------------';

    cmds.push(ESC, 0x40); // Init
    
    if (printType === 'coupon1') {
      const d = getFormattedRestaurantData('coupon1', 'en');
      alignCenter(); boldOn(); sizeLarge();
      addLine(`COUPON NO: ${d.couponNo}`);
      sizeNormal();
      addLine(d.hotelName);
      if (d.branchName) addLine(d.branchName);
      boldOff();
      addLine(separator);
      alignLeft();
      addLine(`Date: ${d.date}  Time: ${d.time}`);
      addLine(separator);
      boldOn();
      addLine(`Item                      Qty   Rate`);
      boldOff();
      addLine(separator);
      d.items.forEach(item => {
        let name = item.name.substring(0, 20);
        let padding = ' '.repeat(Math.max(0, 22 - name.length));
        addLine(`${name}${padding}${item.qtyRate}`);
      });
      addLine(separator);
      boldOn(); sizeLarge();
      addLine(`TOTAL: Rs.${d.totalAmt}`);
      sizeNormal(); boldOff();
      addLine(separator);
      alignCenter(); boldOn();
      addLine(d.footerVisit);
      addLine(d.footerBrand);
    } else if (printType === 'coupon2') {
      const d = getFormattedRestaurantData('coupon2', 'en');
      alignCenter(); boldOn(); sizeLarge();
      addLine(`COUPON #${d.couponNo}`);
      sizeNormal();
      addLine(d.hotelName);
      if (d.branchName) addLine(d.branchName);
      boldOff();
      addLine(separator);
      alignLeft();
      addLine(`Date: ${d.date}  Time: ${d.time}`);
      addLine(separator);
      boldOn();
      addLine(`Item                      Qty   Amt`);
      boldOff();
      addLine(separator);
      d.items.forEach(item => {
        let name = item.name.substring(0, 20);
        let padding = ' '.repeat(Math.max(0, 22 - name.length));
        addLine(`${name}${padding}${item.quantity}   Rs.${item.total}`);
      });
      addLine(separator);
      boldOn(); sizeLarge();
      addLine(`TOTAL: Rs.${d.totalAmt}`);
      sizeNormal(); boldOff();
      addLine(separator);
      alignCenter(); boldOn();
      addLine(d.footerVisit);
      addLine(d.footerBrand);
    } else if (printType === 'kot') {
      const d = getFormattedRestaurantData('kot', 'en'); // Force English for native text
      alignCenter(); boldOn(); sizeLarge();
      addLine(d.header);
      sizeNormal(); boldOff();
      addLine(separator);
      alignLeft(); boldOn();
      addLine(`${d.tableLabel} ${d.tableValue}`);
      boldOff();
      addLine(`${d.idLabel}: ${d.idValue}  ${d.timeLabel}: ${d.timeValue}`);
      addLine(`${d.waiterLabel}: ${d.waiterValue}`);
      addLine(separator);
      boldOn();
      addLine(`Item                            Qty`);
      boldOff();
      addLine(separator);
      d.items.forEach(item => {
        let name = item.name.substring(0, 28);
        let padding = ' '.repeat(Math.max(0, 30 - name.length));
        addLine(`${name}${padding} x ${item.quantity}`);
        if (item.remarks) {
           addLine(` * ${item.remarks}`);
        }
      });
      addLine(separator);
      alignCenter();
      addLine(d.footer);
    } else {
      const d = getFormattedRestaurantData('counter', 'en');
      alignCenter(); boldOn(); sizeLarge();
      addLine(d.header);
      sizeNormal(); boldOff();
      addLine(d.address);
      addLine(d.tel);
      addLine(separator);
      alignLeft();
      addLine(`${d.dateLabel}: ${d.dateValue}  ${d.timeLabel}: ${d.timeValue}`);
      boldOn();
      addLine(`${d.tokenLabel}: ${d.tokenValue}`);
      boldOff();
      addLine(`${d.typeLabel}: ${d.typeValue}  ${d.paymentLabel}: ${d.paymentValue}`);
      addLine(separator);
      boldOn();
      addLine(`Item                      Qty   Amt`);
      boldOff();
      addLine(separator);
      d.items.forEach(item => {
        let name = item.name.substring(0, 20);
        let padding = ' '.repeat(Math.max(0, 22 - name.length));
        addLine(`${name}${padding}${item.quantity}   Rs${item.total.toFixed(0)}`);
      });
      addLine(separator);
      addLine(`${d.subtotalLabel}: Rs${d.subtotalValue.toFixed(0)}`);
      addLine(`${d.gstLabel}: Rs${d.gstValue.toFixed(0)}`);
      if (d.discountValue > 0) {
        addLine(`${d.discountLabel}: -Rs${d.discountValue.toFixed(0)}`);
      }
      boldOn(); sizeLarge();
      addLine(`${d.totalLabel}: Rs${d.totalValue.toFixed(0)}`);
      sizeNormal(); boldOff();
      addLine(separator);
      alignCenter(); boldOn();
      addLine(d.footer);
    }
    
    const feedCount = typeof feedLines === 'number' ? Math.max(0, Math.min(15, feedLines)) : 3;
    cmds.push(ESC, 0x32);
    if (feedCount > 0) {
      for (let i = 0; i < feedCount; i++) {
        cmds.push(0x0A);
      }
    }
    // Only send hardware cut command on 80mm printers
    if (pageSize === '80mm') {
      cmds.push(GS, 0x56, 0x41, 0x01); // Cut
    }

    return new Uint8Array(cmds);
  };


  // Helper to send Uint8Array to a device in smaller chunks with a small delay to avoid buffer overflow
  const sendDataInChunks = async (writeFn, data, chunkSize = 128, delayMs = 3) => {
    for (let i = 0; i < data.length; i += chunkSize) {
      const chunk = data.slice(i, i + chunkSize);
      await writeFn(chunk);
      if (delayMs > 0) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  };

  // Direct ESC/POS printing wrapper for hardware devices
  const executeDirectEscPosPrint = async (deviceType, deviceId) => {
    // Generate ESC/POS byte array either as raw text or rendered canvas
    const binaryData = nativeTextPrint ? generateEscPosTextBytes(pageSize) : generateEscPosCanvasBytes(pageSize);

    // Look for matching real connected device
    const paired = connectedDevices.find(d => d.id === deviceId);

    if (!paired) {
      // Mock Print Simulator Mode
      setTimeout(() => {
        triggerSnackbar(
          `[Direct Print Simulation] Sent ${binaryData.length} raw ESC/POS bytes directly to ${deviceType.toUpperCase()} printer [${deviceId}]. Bypassed print dialog successfully!`,
          'success'
        );
      }, 500);
      return;
    }

    try {
      if (deviceType === 'usb') {
        const device = paired.rawDevice;
        if (!device.opened) {
          await device.open();
        }
        await device.selectConfiguration(1);
        await device.claimInterface(0);

        // Find endpoint
        let endpoint = null;
        const endpoints = device.configuration.interfaces[0].alternates[0].endpoints;
        for (const ep of endpoints) {
          if (ep.direction === 'out') {
            endpoint = ep;
            break;
          }
        }

        if (!endpoint) {
          throw new Error('Bulk Out endpoint not found on USB device.');
        }

        // Send USB data based on memory settings
        if (printerMemoryType === 'high-memory') {
          await device.transferOut(endpoint.endpointNumber, binaryData);
        } else {
          await sendDataInChunks((chunk) => device.transferOut(endpoint.endpointNumber, chunk), binaryData, 512, 2);
        }
        triggerSnackbar('Receipt printed successfully via USB direct port!', 'success');
      } else if (deviceType === 'bluetooth') {
        await BluetoothPrinterManager.connect();
        
        const writeMethod = BluetoothPrinterManager.characteristic.writeValueWithoutResponse ?
          (chunk) => BluetoothPrinterManager.characteristic.writeValueWithoutResponse(chunk) :
          (chunk) => BluetoothPrinterManager.characteristic.writeValue(chunk);

        // Always stream with 128-byte chunks and 3ms micro-delay for smooth motor flow and zero buffer overrun
        await sendDataInChunks(writeMethod, binaryData, 128, 3);
        triggerSnackbar('Receipt printed successfully via Bluetooth direct interface!', 'success');
      } else if (deviceType === 'serial') {
        const port = paired.rawDevice;
        try {
          if (!port.readable) {
            await port.open({ baudRate: 9600 });
          }
          const writer = port.writable.getWriter();

          // Send based on memory settings
          if (printerMemoryType === 'high-memory') {
            await writer.write(binaryData);
          } else {
            await sendDataInChunks((chunk) => writer.write(chunk), binaryData, 128, 5);
          }

          writer.releaseLock();
          triggerSnackbar('Receipt printed successfully via Serial direct COM port!', 'success');
        } finally {
          try {
            await port.close();
          } catch(e) {}
        }
      }
    } catch (err) {
      let customMsg = `Direct Print Error: ${err.message}`;
      if (err.message.includes('The device was disconnected') || (err.message.includes('open') && err.message.includes('USBDevice')) || err.message.includes('claimInterface') || err.message.includes('Access denied')) {
        customMsg = `⚠️ USB प्रिंटर Windows ड्रायव्हरद्वारे आधीच लॉक आहे! Windows वर प्रिंट करण्यासाठी कृपया Connection Type मध्ये 'Browser Default / System Print' निवडा (किंवा Bluetooth वापरा).`;
      } else if (err.message.includes('Failed to open serial port') || err.message.includes('open failed')) {
        customMsg = `Direct Print Error: Serial Port (COM) is already in use by a Windows driver or another app. Please switch to 'Browser Default / System Print' connection type, or close other printing applications.`;
      }
      triggerSnackbar(customMsg, 'warning');
    }
  };

  // Main Test Print action dispatcher
  const handleTestPrint = () => {
    if (connectionType === 'system') {
      if (directPrint) {
        triggerSnackbar('System print requested. Direct kiosk print is bypass-configured. (Note: Bypassing browser printer dialog requires Chrome in Kiosk Mode)', 'info');
      }
      executeSystemPrint();
    } else {
      // Direct print to USB, Bluetooth, or Serial
      executeDirectEscPosPrint(connectionType, selectedPrinterId);
    }
  };

  // Thermal paper roll cut clip path styling
  const thermalPaperStyle = {
    backgroundColor: '#fffdf4',
    color: '#1a1a1a',
    fontFamily: '"Courier New", Courier, monospace',
    fontSize: `${fontSize}px`,
    padding: `${receiptMargins + 12}px ${receiptMargins + 16}px`,
    boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
    border: '1px solid #e3e3cb',
    borderRadius: '4px 4px 0 0',
    position: 'relative',
    margin: '0 auto',
    width: pageSize === '58mm' ? '260px' : '340px',
    transition: 'all 0.3s ease',
    // jagged bottom cut:
    clipPath: 'polygon(0% 0%, 100% 0%, 100% calc(100% - 10px), 98% 100%, 96% calc(100% - 10px), 94% 100%, 92% calc(100% - 10px), 90% 100%, 88% calc(100% - 10px), 86% 100%, 84% calc(100% - 10px), 82% 100%, 80% calc(100% - 10px), 78% 100%, 76% calc(100% - 10px), 74% 100%, 72% calc(100% - 10px), 70% 100%, 68% calc(100% - 10px), 66% 100%, 64% calc(100% - 10px), 62% 100%, 60% calc(100% - 10px), 58% 100%, 56% calc(100% - 10px), 54% 100%, 52% calc(100% - 10px), 50% 100%, 48% calc(100% - 10px), 46% 100%, 44% calc(100% - 10px), 42% 100%, 40% calc(100% - 10px), 38% 100%, 36% calc(100% - 10px), 34% 100%, 32% calc(100% - 10px), 30% 100%, 28% calc(100% - 10px), 26% 100%, 24% calc(100% - 10px), 22% 100%, 20% calc(100% - 10px), 18% 100%, 16% calc(100% - 10px), 14% 100%, 12% calc(100% - 10px), 10% 100%, 8% calc(100% - 10px), 6% 100%, 4% calc(100% - 10px), 2% 100%, 0% calc(100% - 10px))',
  };

  return (
    <Box sx={{ p: 4, bgcolor: '#f9fafb', minHeight: 'calc(100vh - 70px)' }}>
      {/* Page Title */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: '#111827', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PrintIcon sx={{ color: '#008751', fontSize: 32 }} />
            Printer Setup & Configuration (प्रिंटर जोडणी व टेस्ट)
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
            Pair Bluetooth, USB, or Serial POS printers, customize ticket sizes, select document templates, and run diagnostics.
          </Typography>
        </Box>
      </Box>

      {/* Hidden printing iframe target */}
      <iframe
        ref={printFrameRef}
        style={{ display: 'none', position: 'absolute', width: 0, height: 0, border: 'none' }}
        title="print_frame"
      />

      <Grid container spacing={4}>

        {/* Left Column: Printer Configuration Form */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 3 }}>

              <Typography variant="h6" sx={{ color: '#111827', fontWeight: 800, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon sx={{ color: '#008751' }} /> Connection Settings
              </Typography>

              {/* 1. Interface connection type */}
              <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                <InputLabel id="connection-type-label">Connection Type</InputLabel>
                <Select
                  labelId="connection-type-label"
                  value={connectionType}
                  label="Connection Type"
                  onChange={(e) => {
                    setConnectionType(e.target.value);
                    if (e.target.value === 'system') {
                      setSelectedPrinterId('default');
                    }
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="system">🌐 Browser Default / System Print</MenuItem>
                  <MenuItem value="usb">🔌 USB Connection (Direct ESC/POS)</MenuItem>
                  <MenuItem value="bluetooth">🔵 Bluetooth Connection (Direct ESC/POS)</MenuItem>
                  <MenuItem value="serial">📟 Serial COM Port (Direct ESC/POS)</MenuItem>
                </Select>
              </FormControl>

              {/* 2. Print Sample Type selection */}
              <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                <InputLabel id="print-type-label">Select Print Sample (प्रिंट प्रकार)</InputLabel>
                <Select
                  labelId="print-type-label"
                  value={printType}
                  label="Select Print Sample (प्रिंट प्रकार)"
                  onChange={(e) => {
                    const v = e.target.value;
                    setPrintType(v);
                    if (['coupon1', 'coupon2', 'format1', 'format2'].includes(v)) {
                      handleTemplateChange(v);
                    }
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="coupon1">🎫 Coupon 1 (हॉटेल नाव + बॉक्स कूपन टोकन)</MenuItem>
                  <MenuItem value="coupon2">🎟️ Coupon 2 (सेंटर्ड बिग कूपन टोकन)</MenuItem>
                  <MenuItem value="format1">Template 1: Minimalist (Single Line Items)</MenuItem>
                  <MenuItem value="format2">Template 2: Bold Graphic (Table Format)</MenuItem>
                  <MenuItem value="counter">🟢 Quick POS Counter Bill (काउंटर बिल)</MenuItem>
                  <MenuItem value="kot">🔴 Kitchen Order Ticket (KOT / के.ओ.टी.)</MenuItem>
                </Select>
              </FormControl>

              {/* 3. Printer selection dropdown & scan option */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, alignItems: 'flex-start' }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="printer-select-label">Select Printer</InputLabel>
                  <Select
                    labelId="printer-select-label"
                    value={selectedPrinterId}
                    label="Select Printer"
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedPrinterId(val);
                      if (val === 'default') {
                        setConnectionType('system');
                      } else {
                        const matched = connectedDevices.find(d => d.id === val);
                        if (matched) {
                          setConnectionType(matched.type);
                        }
                      }
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    {printersList.map((printer) => (
                      <MenuItem key={printer.id} value={printer.id}>
                        {printer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {connectionType !== 'system' && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={connectionType === 'usb' ? <UsbIcon /> : connectionType === 'bluetooth' ? <BluetoothIcon /> : <RefreshIcon />}
                    onClick={handlePairPrinter}
                    sx={{ height: 40, whiteSpace: 'nowrap', textTransform: 'none', borderRadius: 2, bgcolor: '#008751', '&:hover': { bgcolor: '#006f42' } }}
                  >
                    Pair Printer
                  </Button>
                )}
              </Box>

              {/* 4. Page Type / Size selection */}
              <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                <InputLabel id="page-size-label">Page / Receipt Size</InputLabel>
                <Select
                  labelId="page-size-label"
                  value={pageSize}
                  label="Page / Receipt Size"
                  onChange={(e) => setPageSize(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="58mm">POS Thermal Receipt 58mm (2 Inch)</MenuItem>
                  <MenuItem value="80mm">POS Thermal Receipt 80mm (3 Inch)</MenuItem>
                </Select>
              </FormControl>

              {/* 5. Receipt Print Language Selection */}
              <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                <InputLabel id="print-language-label">Receipt Print Language</InputLabel>
                <Select
                  labelId="print-language-label"
                  value={printLanguage}
                  label="Receipt Print Language"
                  onChange={(e) => setPrintLanguage(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="mr">मराठी (Marathi)</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="hi">हिन्दी (Hindi)</MenuItem>
                </Select>
              </FormControl>

              {/* 6. Print Density / Darkness Selection */}
              <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                <InputLabel id="print-density-label">Print Density / Darkness (प्रिंट डार्कनेस)</InputLabel>
                <Select
                  labelId="print-density-label"
                  value={printDensity}
                  label="Print Density / Darkness (प्रिंट डार्कनेस)"
                  onChange={(e) => setPrintDensity(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="normal">Normal (सामान्य)</MenuItem>
                  <MenuItem value="dark">Dark (गडद - शिफारस केलेले)</MenuItem>
                  <MenuItem value="extra-dark">Extra Dark (खूप गडद / स्पष्ट)</MenuItem>
                </Select>
              </FormControl>

              {/* 7. Receipt Template Format */}
              <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                <InputLabel id="receipt-template-label">Receipt Print Layout / Template</InputLabel>
                <Select
                  labelId="receipt-template-label"
                  value={receiptTemplate}
                  label="Receipt Print Layout / Template"
                  onChange={handleTemplateChange}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="coupon1">Coupon 1: Snack Token with Box (शिफारस केलेले)</MenuItem>
                  <MenuItem value="coupon2">Coupon 2: Compact Centered Token</MenuItem>
                  <MenuItem value="format1">Template 1: Minimalist (Single Line Items)</MenuItem>
                  <MenuItem value="format2">Template 2: Bold Graphic (Table Format)</MenuItem>
                </Select>
              </FormControl>

              {/* 8. Paper Line Feed Selection */}
              <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                <InputLabel id="feed-lines-label">Paper Feed Lines (प्रिंट नंतर कागद लाईन फीड)</InputLabel>
                <Select
                  labelId="feed-lines-label"
                  value={feedLines}
                  label="Paper Feed Lines (प्रिंट नंतर कागद लाईन फीड)"
                  onChange={handleFeedLinesChange}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value={0}>0 Lines (नो फीड / No Feed)</MenuItem>
                  <MenuItem value={1}>1 Line</MenuItem>
                  <MenuItem value={2}>2 Lines</MenuItem>
                  <MenuItem value={3}>3 Lines (Standard / डीफॉल्ट)</MenuItem>
                  <MenuItem value={4}>4 Lines</MenuItem>
                  <MenuItem value={5}>5 Lines (शिफारस केलेले)</MenuItem>
                  <MenuItem value={6}>6 Lines</MenuItem>
                  <MenuItem value={7}>7 Lines</MenuItem>
                  <MenuItem value={8}>8 Lines</MenuItem>
                  <MenuItem value={9}>9 Lines</MenuItem>
                  <MenuItem value={10}>10 Lines (जास्त अंतर / Extra Space)</MenuItem>
                </Select>
              </FormControl>

              <Divider sx={{ my: 3 }} />

              {/* Formatting & Stylings (Thermal POS Only) */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: '#475569' }}>
                  Receipt Layout Formatting
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography id="font-size-slider" variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                      Font Size: {fontSize}px
                    </Typography>
                    <Slider
                      value={fontSize}
                      onChange={(e, val) => {
                        setFontSize(val);
                        localStorage.setItem('dinexa_printer_font_size', String(val));
                      }}
                      min={8}
                      max={18}
                      step={1}
                      valueLabelDisplay="auto"
                      aria-labelledby="font-size-slider"
                      sx={{ color: '#008751' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography id="margin-slider" variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                      Left Margin: {receiptMargins}px
                    </Typography>
                    <Slider
                      value={receiptMargins}
                      onChange={(e, val) => {
                        setReceiptMargins(val);
                        localStorage.setItem('dinexa_printer_margin_offset', String(val));
                      }}
                      min={0}
                      max={30}
                      step={1}
                      valueLabelDisplay="auto"
                      aria-labelledby="margin-slider"
                      sx={{ color: '#008751' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography id="width-slider" variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                      Printable Width: {printableWidth}px
                    </Typography>
                    <Slider
                      value={printableWidth}
                      onChange={(e, val) => {
                        setPrintableWidth(val);
                        localStorage.setItem('dinexa_printer_content_width', String(val));
                      }}
                      min={240}
                      max={360}
                      step={5}
                      valueLabelDisplay="auto"
                      aria-labelledby="width-slider"
                      sx={{ color: '#008751' }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Direct Print Toggle */}
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2.5, border: '1px solid #e2e8f0', mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={directPrint}
                      onChange={(e) => setDirectPrint(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        Direct Print (Bypass system dialog box)
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.2 }}>
                        Prints instantly without launching the system setup box.
                      </Typography>
                    </Box>
                  }
                />

                {directPrint && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={nativeTextPrint}
                          onChange={(e) => setNativeTextPrint(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b' }}>
                            Native Text Print (Fast English Mode)
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.2 }}>
                            Prints ultra-fast using raw text. WARNING: Select this only if language is English! Marathi characters will print as ??.
                          </Typography>
                        </Box>
                      }
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={printerMemoryType === 'high-memory'}
                          onChange={(e) => setPrinterMemoryType(e.target.checked ? 'high-memory' : 'low-memory')}
                          color="secondary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', mt: 1 }}>
                            High Memory Printer
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.2 }}>
                            Enable this for premium printers (Epson/Zebra) to print without chunking delays.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                )}
              </Box>

              {/* Save & Apply Settings Button */}
              <Button
                fullWidth
                variant="contained"
                onClick={handleSaveAllSettings}
                startIcon={<SettingsIcon />}
                sx={{ height: 48, fontWeight: 900, fontSize: 15, borderRadius: 2, textTransform: 'none', mt: 3, bgcolor: '#0f172a', color: '#ffffff', '&:hover': { bgcolor: '#1e293b' } }}
              >
                💾 प्रिंटर सेटिंग्ज सेव्ह व लागू करा (Save & Apply Settings)
              </Button>

              {/* Test Print Action */}
              <Button
                fullWidth
                variant="outlined"
                onClick={handleTestPrint}
                startIcon={<PrintIcon />}
                sx={{ height: 46, fontWeight: 800, fontSize: 14, borderRadius: 2, textTransform: 'none', mt: 1.5, borderColor: '#008751', color: '#008751', '&:hover': { bgcolor: '#f0fdf4', borderColor: '#006f42' } }}
              >
                🖨️ टेस्ट प्रिंट काढून पहा (Test Print)
              </Button>

            </CardContent>
          </Card>
          
          {/* Diagnostic Guide Card */}
          <Card sx={{ mt: 3, borderRadius: 3, border: '1px solid #e5e7eb', bgcolor: '#f8fafc', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ color: '#111827', fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                💡 मार्गदर्शन आणि मदत (Troubleshooting Guide)
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#008751', mb: 0.5 }}>
                    १. Windows Printer (उदा. Everycom-58) साठी सेटिंग:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4b5563', fontSize: '13px', pl: 1.5 }}>
                    जर प्रिंटर Windows च्या 'Printers & Scanners' मध्ये जोडलेला असेल, तर <strong>Connection Type</strong> मध्ये <strong>🌐 Browser Default / System Print</strong> निवडा. यामुळे तुमच्या थर्मल प्रिंटरवर अचूक प्रिंट येईल.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#008751', mb: 0.5 }}>
                    २. डायलॉग बॉक्स न दाखवता थेट प्रिंट (Direct Print) कशी करावी?
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4b5563', fontSize: '13px', pl: 1.5 }}>
                    सिस्टम प्रिंटचा डायलॉग न दाखवता एका क्लिकवर थेट प्रिंट मिळवण्यासाठी <strong>Direct Print</strong> चेकबॉक्स टिक करा आणि क्रोम च्या डेस्कटॉप शॉर्टकटवर राईट क्लिक करून Target फील्डच्या शेवटी <code>--kiosk-printing</code> जोडा.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#008751', mb: 0.5 }}>
                    ३. USB / Bluetooth / Serial डायरेक्ट कनेक्शन:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4b5563', fontSize: '13px', pl: 1.5 }}>
                    जर ब्लूटूथ प्रिंटर मोबाईल किंवा पीसी वरून पेअर होत नसेल, तर <strong>Bluetooth Connection</strong> निवडून <strong>Pair Printer</strong> दाबा. हे थेट क्रोम च्या वेब ब्लूटूथ स्कॅनरचा वापर करते ज्यामुळे पेअरिंग मधील सर्व समस्या दूर होतात.
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Live Mock Preview */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

            {/* Header for preview section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#475569', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                👀 Live Print Preview
              </Typography>
              <Typography variant="caption" sx={{ bgcolor: '#e2e8f0', color: '#475569', px: 1.5, py: 0.5, borderRadius: 4, fontWeight: 'bold' }}>
                Format: {pageSize.toUpperCase()} (${printLanguage.toUpperCase()})
              </Typography>
            </Box>

            {/* Preview Box Container */}
            <Box sx={{
              flexGrow: 1,
              bgcolor: '#e2e8f0',
              border: '2px dashed #cbd5e1',
              borderRadius: 3,
              p: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '450px',
              overflowY: 'auto'
            }}>

              {printType === 'coupon1' ? (
                /* Coupon 1 (Matching Screenshot 2 / User Diagram) Preview Layout */
                <Box sx={{
                  ...thermalPaperStyle,
                  borderTop: '6px dashed #008751',
                  borderBottom: '6px dashed #008751',
                  backgroundColor: '#ffffff',
                  color: '#000000',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                }}>
                  {(() => {
                    const d = getFormattedRestaurantData('coupon1', printLanguage);
                    return (
                      <Box sx={{ fontFamily: "'Noto Sans Devanagari', 'Mukta', 'Courier New', sans-serif", color: '#000000', width: '100%', boxSizing: 'border-box' }}>
                        {/* Top Header: Left Hotel Name, Right Box Coupon No */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ textAlign: 'left', flex: 1, pr: 1 }}>
                            <Typography sx={{ fontWeight: 900, fontSize: '1.2rem', lineHeight: 1.15, color: '#000000' }}>
                              {d.hotelName}
                            </Typography>
                            {d.branchName && (
                              <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#475569', mt: 0.3 }}>
                                {d.branchName}
                              </Typography>
                            )}
                          </Box>

                          <Box sx={{ 
                            border: '2px solid #000000', 
                            borderRadius: 1, 
                            px: 1.5, 
                            py: 0.5, 
                            textAlign: 'center',
                            minWidth: 70,
                            bgcolor: '#f8fafc'
                          }}>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#000000', lineHeight: 1 }}>
                              Coupon No.
                            </Typography>
                            <Typography sx={{ fontSize: '1.8rem', fontWeight: 900, color: '#000000', lineHeight: 1.1, mt: 0.3 }}>
                              {d.couponNo}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Date & Time Row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', fontWeight: 800, my: 0.5 }}>
                          <span>दिनांक: {d.date}</span>
                          <span>{d.time}</span>
                        </Box>

                        <Box sx={{ borderBottom: '1.5px dashed #000000', my: 1 }} />

                        {/* Column Header: पदार्थ | Qty * Rate */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.92rem', fontWeight: 900, mb: 0.5 }}>
                          <span>पदार्थ</span>
                          <span>Qty * Rate</span>
                        </Box>

                        {/* Items */}
                        {d.items.map((item, idx) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', fontWeight: 800, py: 0.4 }}>
                            <span>{item.name}</span>
                            <span>{item.qtyRate}</span>
                          </Box>
                        ))}

                        <Box sx={{ borderBottom: '1.5px dashed #000000', my: 1 }} />

                        {/* Total Amount in Large Sharp Font */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                          <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, color: '#000000' }}>
                            एकूण बिल रक्कम
                          </Typography>
                          <Typography sx={{ fontSize: '1.45rem', fontWeight: 900, color: '#000000' }}>
                            {d.totalAmt}
                          </Typography>
                        </Box>

                        <Box sx={{ borderBottom: '1.5px dashed #000000', my: 1 }} />

                        {/* Centered Footer */}
                        <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#000000' }}>
                            {d.footerVisit}
                          </Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: '#000000', mt: 0.2 }}>
                            {d.footerBrand}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              ) : printType === 'coupon2' ? (
                /* Coupon 2 Preview Layout */
                <Box sx={{
                  ...thermalPaperStyle,
                  borderTop: '6px dashed #0284c7',
                  borderBottom: '6px dashed #0284c7',
                  backgroundColor: '#ffffff',
                  color: '#000000',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                }}>
                  {(() => {
                    const d = getFormattedRestaurantData('coupon2', printLanguage);
                    return (
                      <Box sx={{ fontFamily: "'Noto Sans Devanagari', 'Mukta', 'Courier New', sans-serif", color: '#000000', width: '100%', boxSizing: 'border-box' }}>
                        {/* Centered Token Header Badge (White bg + 2px black border + bold black text) */}
                        <Box sx={{ bgcolor: '#ffffff', color: '#000000', border: '2px solid #000000', py: 0.8, px: 1, borderRadius: 1.5, textAlign: 'center', mb: 1 }}>
                          <Typography sx={{ fontWeight: 900, fontSize: '1.25rem', letterSpacing: 1, color: '#000000' }}>
                            COUPON #{d.couponNo}
                          </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center', mb: 1 }}>
                          <Typography sx={{ fontWeight: 900, fontSize: '1.1rem' }}>
                            {d.hotelName}
                          </Typography>
                          {d.branchName && (
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>
                              Branch: {d.branchName}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', fontWeight: 800, my: 0.5 }}>
                          <span>दिनांक: {d.date}</span>
                          <span>{d.time}</span>
                        </Box>

                        <Box sx={{ borderBottom: '1.5px dashed #000000', my: 1 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', fontWeight: 900, mb: 0.5 }}>
                          <span>पदार्थ</span>
                          <span>नग x दर</span>
                        </Box>

                        {d.items.map((item, idx) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', fontWeight: 800, py: 0.3 }}>
                            <span>{item.name}</span>
                            <span>{item.quantity} x ₹{item.price}</span>
                          </Box>
                        ))}

                        <Box sx={{ borderBottom: '1.5px dashed #000000', my: 1 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 900 }}>
                            एकूण रक्कम :
                          </Typography>
                          <Typography sx={{ fontSize: '1.3rem', fontWeight: 900 }}>
                            ₹{d.totalAmt}
                          </Typography>
                        </Box>

                        <Box sx={{ borderBottom: '1.5px dashed #000000', my: 1 }} />

                        <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800 }}>
                            {d.footerVisit}
                          </Typography>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', mt: 0.2 }}>
                            {d.footerBrand}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              ) : (
                /* POS Thermal Roll Preview Layout */
                <Box sx={thermalPaperStyle}>
                  {(() => {
                    const d = getFormattedRestaurantData('counter', printLanguage);
                    const separatorLine = pageSize === '58mm' ? '============================' : '========================================';
                    return (
                      <Box sx={{ fontFamily: "'Courier New', Courier, monospace", color: '#000', fontSize: `${fontSize}px`, width: '100%', boxSizing: 'border-box' }}>
                        <Box sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.2em', mb: 0.5 }}>
                          {d.header}
                        </Box>
                        <Box sx={{ textAlign: 'center', fontSize: '0.8em', mb: 1 }}>
                          {d.address}<br/>{d.tel}
                        </Box>

                        <Box sx={{ letterSpacing: '-0.5px', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                          {separatorLine}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85em', mb: 0.5 }}>
                          <div>{d.dateLabel}: {d.dateValue}</div>
                          <div>{d.timeLabel}: {d.timeValue}</div>
                        </Box>
                        <Box sx={{ fontWeight: 'bold', fontSize: '1.05em', mb: 0.5 }}>
                          {d.tokenLabel}: {d.tokenValue}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85em', mb: 1.5 }}>
                          <div>{d.typeLabel}: {d.typeValue}</div>
                          <div>{d.paymentLabel}: {d.paymentValue}</div>
                        </Box>

                        <Box sx={{ letterSpacing: '-0.5px', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                          {separatorLine}
                        </Box>

                        <Box sx={{ display: 'flex', fontWeight: 'bold', fontSize: '0.85em', mb: 0.5 }}>
                          <span style={{ flexGrow: 1 }}>Item</span>
                          <span style={{ width: 40, textAlign: 'center' }}>Qty</span>
                          <span style={{ width: 60, textAlign: 'right' }}>Amt</span>
                        </Box>
                        <Box sx={{ letterSpacing: '-0.5px', mb: 1, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                          {separatorLine}
                        </Box>

                        {d.items.map((item, idx) => (
                          <Box key={idx} sx={{ display: 'flex', py: 0.4 }}>
                            <span style={{ flexGrow: 1 }}>{item.name}</span>
                            <span style={{ width: 40, textAlign: 'center' }}>{item.quantity}</span>
                            <span style={{ width: 60, textAlign: 'right' }}>₹{item.total.toFixed(0)}</span>
                          </Box>
                        ))}

                        <Box sx={{ letterSpacing: '-0.5px', my: 1, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                          {separatorLine}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                          <span>{d.subtotalLabel}</span>
                          <span>₹{d.subtotalValue.toFixed(0)}</span>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                          <span>{d.gstLabel}</span>
                          <span>₹{d.gstValue.toFixed(0)}</span>
                        </Box>
                        {d.discountValue > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2, color: 'error.main' }}>
                            <span>{d.discountLabel}</span>
                            <span>-₹{d.discountValue.toFixed(0)}</span>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '1.05em', mt: 1, pt: 1, borderTop: '1px solid #1e293b' }}>
                          <span>{d.totalLabel}</span>
                          <span>₹{d.totalValue.toFixed(0)}</span>
                        </Box>

                        <Box sx={{ letterSpacing: '-0.5px', mt: 1, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                          {separatorLine}
                        </Box>
                        <Box sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '0.9em', mt: 1 }}>
                          {d.footer}
                        </Box>
                        <Box sx={{ textAlign: 'center', fontSize: '0.8em', color: '#64748b', mt: 0.5 }}>
                          Powered by Dinexa POS
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              )}

            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        action={
          <Button color="secondary" size="small" onClick={() => setSnackbarOpen(false)}>
            Dismiss
          </Button>
        }
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: snackbarSeverity === 'success' ? '#2e7d32'
              : snackbarSeverity === 'error' ? '#d32f2f'
                : snackbarSeverity === 'warning' ? '#ed6c02'
                  : '#0288d1'
          }
        }}
      />
    </Box>
  );
};

export default PrinterUtility;
